import { Row } from '@amzn/stencil-react-components/layout';
import { Text } from '@amzn/stencil-react-components/text';
import { IconCalendarSmall } from '@amzn/stencil-react-icons';
import { getDueDateColor } from '../domain';

interface DueDateDisplayProps {
  /** Formatted date label (e.g. "Jul 9, 2026"). Pass '-' or omit to render nothing. */
  dueDateLabel: string;
  /** Whether the item is past its due date. */
  isOverdue: boolean;
  /** Whether the item is approaching its due date (within the urgent threshold). */
  isUrgent: boolean;
  /** Translated label for the due date (e.g. "Due Jul 9, 2026"). */
  label: string;
  /** Translated label shown when overdue instead of the date. */
  overdueLabel: string;
}

/**
 * Due date row with calendar icon and urgency color.
 * Used by AuditCard and TaskCard — keeps icon + urgency logic in one place.
 *
 * Renders nothing when dueDateLabel is '-'.
 *
 * @example
 * ```tsx
 * <DueDateDisplay
 *   dueDateLabel={formatDisplayDate(task.dueDate)}
 *   isOverdue={isOverdue}
 *   isUrgent={isUrgent}
 *   label={f('dueDate', { date: dueDateLabel })}
 *   overdueLabel={t('overdue')}
 * />
 * ```
 */
export function DueDateDisplay({
  dueDateLabel,
  isOverdue,
  isUrgent,
  label,
  overdueLabel,
}: DueDateDisplayProps) {
  if (dueDateLabel === '-') return null;

  const color = getDueDateColor(isOverdue, isUrgent);

  return (
    <Row alignItems="center" gridGap="dimensions.spacing.100">
      <IconCalendarSmall color={color} aria-hidden />
      <Text color={color}>{isOverdue ? overdueLabel : label}</Text>
    </Row>
  );
}
